// src/screens/Admin/navigation screens/support/AdminSupportTickets.jsx
import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { toast, ToastContainer } from "react-toastify";
import MyContext from "../../../MyContext";
import TicketDetailDrawer from "./TicketDetailDrawer";
import { StatusBadge, PriorityBadge } from "./TicketBadges";
import NoIndex from "../../NoIndex";
import "./AdminTicket.css";

function AdminSupportTickets() {
  const { baseUrl, config } = useContext(MyContext);

  // list + ui state
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [deptFilter, setDeptFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [priorityFilter, setPriorityFilter] = useState("ALL");

  // pagination
  const [page, setPage] = useState(1);
  const perPage = 20;

  // drawer (detail)
  const [activeTicket, setActiveTicket] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const fetchTickets = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const payload = { userid: "" }; // admin view — send empty as per your convention
      const { data } = await axios.post(
        `${baseUrl}/api/Support/v1.0/getAllSupportTicket`,
        payload,
        config
      );
      if (data.responseCode === "00") {
        setTickets(data.response || []);
      } else {
        toast.error(data.responseMessage || "Failed to fetch tickets");
        setTickets([]);
      }
    } catch (e) {
      toast.error("Error fetching tickets");
      console.error(e);
      setTickets([]);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  // filters
  const filtered = useMemo(() => {
    const s = search.toLowerCase();
    return tickets.filter((t) => {
      const inSearch =
        (t.ticket_code || "").toLowerCase().includes(s) ||
        (t.subject || "").toLowerCase().includes(s) ||
        (t.description || "").toLowerCase().includes(s) ||
        (t.department || "").toLowerCase().includes(s) ||
        (t.priority || "").toLowerCase().includes(s) ||
        (t.status || "").toLowerCase().includes(s) ||
        (t.customer_id || "").toLowerCase().includes(s);

      const byDept = deptFilter === "ALL" || t.department === deptFilter;
      const byStatus = statusFilter === "ALL" || t.status === statusFilter;
      const byPriority =
        priorityFilter === "ALL" || t.priority === priorityFilter;

      return inSearch && byDept && byStatus && byPriority;
    });
  }, [tickets, search, deptFilter, statusFilter, priorityFilter]);

  // pagination slice
  const totalPages = Math.ceil(filtered.length / perPage) || 1;
  const current = filtered.slice((page - 1) * perPage, page * perPage);

  const openTicket = (ticket) => {
    setActiveTicket(ticket);
    setDrawerOpen(true);
  };

  const closeDrawer = (refetch = false) => {
    setDrawerOpen(false);
    setActiveTicket(null);
    if (refetch) fetchTickets(true);
  };

  // guess lists for filters from data
  const departments = useMemo(() => {
    const s = new Set();
    tickets.forEach((t) => t.department && s.add(t.department));
    return ["ALL", ...[...s]];
  }, [tickets]);

  // using common short status codes (O=Open, P=InProgress, C=Closed) — your sample shows "C"
  const statusOptions = ["ALL", "O", "P", "C"];

  const priorityOptions = useMemo(() => {
    const s = new Set();
    tickets.forEach((t) => t.priority && s.add(t.priority));
    return ["ALL", ...[...s]];
  }, [tickets]);

  return (
    <div className="marginRight admin-ticket-main">
      <NoIndex />
      <ToastContainer />

      <div className="admin-ticket-container ">
        <div className="d-flex align-items-center justify-content-between flex-wrap mb-3 border rounded-2 bg-light p-3">
          <h2 className="mb-2">Support Tickets</h2>

          <div className="d-flex gap-2 flex-wrap">
            <input
              className="form-control admin-ticket-search"
              placeholder="Search code / subject / description / customer..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              style={{ minWidth: 260 }}
            />
            <select
              className="form-select"
              value={deptFilter}
              onChange={(e) => {
                setDeptFilter(e.target.value);
                setPage(1);
              }}
            >
              {departments.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
            <select
              className="form-select"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
            >
              {statusOptions.map((s) => (
                <option key={s} value={s}>
                  {s === "ALL" ? "ALL" : s === "O" ? "Open" : s === "P" ? "In Progress" : "Closed"}
                </option>
              ))}
            </select>
            <select
              className="form-select"
              value={priorityFilter}
              onChange={(e) => {
                setPriorityFilter(e.target.value);
                setPage(1);
              }}
            >
              {priorityOptions.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>

            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => fetchTickets()}
              disabled={loading}
              title="Refresh"
            >
              {loading ? "Loading..." : "Refresh"}
            </button>
          </div>
        </div>

        {/* table */}
        <div className="admin-ticket-table-wrap">
          <table className="table table-hover align-middle admin-ticket-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Subject</th>
                <th>Department</th>
                <th>Priority</th>
                <th>Status</th>
                <th>Created</th>
                <th>Updated</th>
                <th className="text-end">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="text-center py-4">
                    <div className="spinner-border" role="status" />
                  </td>
                </tr>
              ) : current.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-4">
                    No tickets found.
                  </td>
                </tr>
              ) : (
                current.map((t) => (
                  <tr key={t.pk_ticket_id} className="admin-ticket-row">
                    <td className="fw-semibold">{t.ticket_code}</td>
                    <td className="text-truncate" style={{ maxWidth: 320 }}>
                      {t.subject}
                      <div className="small text-muted">{t.description}</div>
                    </td>
                    <td>{t.department || "-"}</td>
                    <td><PriorityBadge value={t.priority} /></td>
                    <td><StatusBadge code={t.status} /></td>
                    <td>{t.created_on || "-"}</td>
                    <td>{t.updated_on || "-"}</td>
                    <td className="text-end">
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => openTicket(t)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* pagination */}
        {totalPages > 1 && (
          <div className="d-flex justify-content-center mt-3">
            <ul className="pagination">
              {Array.from({ length: totalPages }, (_, i) => (
                <li
                  className={`page-item ${i + 1 === page ? "active" : ""}`}
                  key={i}
                >
                  <button className="page-link" onClick={() => setPage(i + 1)}>
                    {i + 1}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* drawer */}
      <TicketDetailDrawer
        open={drawerOpen}
        onClose={closeDrawer}
        ticket={activeTicket}
      />
    </div>
  );
}

export default AdminSupportTickets;
